// The contact form component (layout.style = "contact"): the compact card's look
// and shell (inline or floating, the same CSS and theming), with one form:
//
//   [address]  optional: postcode → choose the house (required when switched on)
//   [topic]    optional: "What is your enquiry about?" drop-down
//   name, email, phone (optional), message, consent → Send
//
// The enquiry is emailed to the installer (public API POST /v1/contact) and not
// stored, like callback requests. The same code runs in the console preview
// (with the mock API), so the preview is exactly what visitors see.

import { ensureGoogleFont } from "./fonts";
import { WIDGET_CSS, cssVars } from "./styles";
import type { Address, ContactStep, WidgetApi, WidgetBranding } from "./types";
import type { MountOptions, WidgetHandle } from "./render";

const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function esc(s: string | number): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

const ICON_MAIL =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5 10 17l9-10"/></svg>';
const XENSCOPE_GLYPH =
  '<svg viewBox="0 0 10 18" aria-hidden="true"><path d="M0 0 6.2 3.6Q10 5.8 10 9.2V9.6H0Z" fill="#E1EE2B"/><rect y="11" width="10" height="7" rx="2.4" fill="#014852"/></svg>';

// The form's extra styles (on top of the compact card's)
const CONTACT_CSS = `
.xq-textarea { min-height: 110px; resize: vertical; line-height: 1.45; padding-top: 10px; padding-bottom: 10px; }
.xq-select { width: 100%; }
.xq-chosen { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px;
  border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius); background: var(--xq-input-bg); font-size: 14px; }
.xq-contact-list { max-height: 200px; margin-top: 8px; }
.xq-req { color: var(--xq-muted); font-weight: 400; }
`;

interface State {
  step: ContactStep;
  open: boolean;
  busy: boolean;
  sending: boolean; // the enquiry is being sent (busy is also set by the postcode search)
  error: string | null;
  postcode: string;
  addresses: Address[];
  address: Address | null;
  searched: boolean; // a postcode search has run (show its list)
  topic: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  consent: boolean;
}

export function mountContactForm(host: HTMLElement, opts: MountOptions): WidgetHandle {
  let branding = opts.branding;
  const api: WidgetApi = opts.api;

  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  shadow.innerHTML = "";
  const style = document.createElement("style");
  style.textContent = WIDGET_CSS + CONTACT_CSS;
  const root = document.createElement("div");
  shadow.append(style, root);

  const blank = (): State => ({
    step: "form",
    open: opts.startOpen ?? false,
    busy: false,
    sending: false,
    error: null,
    postcode: "",
    addresses: [],
    address: null,
    searched: false,
    topic: "",
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false,
  });
  const state = blank();

  // ─── Rendering ─────────────────────────────────────────────────────────────

  function render() {
    ensureGoogleFont(branding.font.family);
    for (const [k, v] of Object.entries(cssVars(branding))) root.style.setProperty(k, v);
    root.className = `xq-root${opts.contained ? " xq-contained" : ""}${opts.highlight ? " xq-highlight" : ""}`;
    const l = branding.layout;
    root.innerHTML =
      l.mode === "floating"
        ? `<div class="xq-floating xq-pos-${l.position}">${state.open ? canvas() : `<button type="button" class="xq-launcher" data-action="open">${ICON_MAIL}${esc(l.launcherLabel)}</button>`}</div>`
        : `<div class="xq-inline xq-align-${l.align}">${canvas()}</div>`;
    syncDisabled();
  }

  function canvas(): string {
    const c = branding.copy;
    const logo = branding.logoUrl && /^https:\/\//.test(branding.logoUrl) ? `<img class="xq-logo" src="${esc(branding.logoUrl)}" alt="">` : "";
    const close = branding.layout.mode === "floating" ? `<button type="button" class="xq-close" data-action="close" aria-label="Close">×</button>` : "";
    const head = logo || close ? `<div class="xq-head">${logo}${close}</div>` : "";
    return `<div class="xq-canvas" role="region" aria-label="${esc(c.title)}">
      <div class="xq-body">
        ${head}
        ${state.step === "done" ? doneHtml() : formHtml()}
        ${state.error ? `<p class="xq-error" role="alert">${esc(state.error)}</p>` : ""}
      </div>
      <div class="xq-foot">Powered by <a href="https://xenscope.com" target="_blank" rel="noopener">${XENSCOPE_GLYPH}xenscope</a></div>
    </div>`;
  }

  function addressHtml(): string {
    if (!branding.form.askAddress) return "";
    if (state.address) {
      return `<div class="xq-field"><span class="xq-label">Your address</span>
        <div class="xq-chosen"><span>${esc(state.address.line)}</span><button type="button" class="xq-link" data-action="change-address">Change</button></div></div>`;
    }
    const list = state.searched
      ? state.addresses.length
        ? `<div class="xq-list xq-contact-list">${state.addresses
            .map((a) => `<button type="button" class="xq-option" data-action="pick-address" data-id="${esc(a.id)}">${esc(a.line)}</button>`)
            .join("")}</div>`
        : `<p class="xq-hint">We couldn't find any addresses for that postcode.</p>`
      : "";
    return `<div class="xq-field"><label class="xq-label" for="xqc-postcode">Your postcode</label>
      <div class="xq-row">
        <input class="xq-input" id="xqc-postcode" data-model="postcode" value="${esc(state.postcode)}" placeholder="e.g. SW1A 1AA" autocomplete="postal-code">
        <button type="button" class="xq-btn" data-action="find" data-needs="postcode">${state.busy && !state.searched ? "Searching…" : "Find address"}</button>
      </div>${list}</div>`;
  }

  function formHtml(): string {
    const c = branding.copy;
    const f = branding.form;
    const topics = f.topicEnabled && f.topics.length
      ? `<div class="xq-field"><label class="xq-label" for="xqc-topic">${esc(f.topicLabel)}${f.topicRequired ? "" : ' <span class="xq-req">(optional)</span>'}</label>
          <select class="xq-input xq-select" id="xqc-topic" data-model="topic">
            <option value="">Choose…</option>
            ${f.topics.map((t) => `<option value="${esc(t)}"${state.topic === t ? " selected" : ""}>${esc(t)}</option>`).join("")}
          </select></div>`
      : "";
    return `<h2 class="xq-title">${esc(c.title)}</h2>
      <p class="xq-sub">${esc(c.subtitle)}</p>
      <form data-form="contact" novalidate>
        ${addressHtml()}
        ${topics}
        <div class="xq-field"><label class="xq-label" for="xqc-name">Name</label>
          <input class="xq-input" id="xqc-name" data-model="name" value="${esc(state.name)}" autocomplete="name"></div>
        <div class="xq-field"><label class="xq-label" for="xqc-email">Email</label>
          <input class="xq-input" id="xqc-email" type="email" data-model="email" value="${esc(state.email)}" autocomplete="email"></div>
        <div class="xq-field"><label class="xq-label" for="xqc-phone">Phone <span class="xq-req">(optional)</span></label>
          <input class="xq-input" id="xqc-phone" type="tel" data-model="phone" value="${esc(state.phone)}" autocomplete="tel"></div>
        <div class="xq-field"><label class="xq-label" for="xqc-message">Message</label>
          <textarea class="xq-input xq-textarea" id="xqc-message" data-model="message" maxlength="3000">${esc(state.message)}</textarea></div>
        <label class="xq-consent"><input type="checkbox" data-model="consent" ${state.consent ? "checked" : ""}><span>${esc(c.consentText)}</span></label>
        <button type="submit" class="xq-btn xq-btn-block" data-needs="contact">${state.sending ? "Sending…" : esc(c.buttonText)}</button>
      </form>`;
  }

  function doneHtml(): string {
    const c = branding.copy;
    return `<div class="xq-done">
        <div class="xq-done-icon">${ICON_CHECK}</div>
        <h2 class="xq-title">${esc(c.thankYouTitle)}</h2>
        <p class="xq-sub">${esc(c.thankYouText)}</p>
        <button type="button" class="xq-link" data-action="restart">Send another message</button>
      </div>`;
  }

  /** What's still needed before Send works (the form's options decide). */
  function ready(): boolean {
    const f = branding.form;
    return (
      state.name.trim().length > 1 &&
      EMAIL_RE.test(state.email.trim()) &&
      state.message.trim().length > 1 &&
      state.consent &&
      (!f.askAddress || state.address !== null) &&
      (!(f.topicEnabled && f.topicRequired && f.topics.length) || state.topic !== "")
    );
  }

  function syncDisabled() {
    const valid: Record<string, boolean> = { postcode: POSTCODE_RE.test(state.postcode.trim()), contact: ready() };
    root.querySelectorAll<HTMLButtonElement>("[data-needs]").forEach((b) => {
      b.disabled = state.busy || !valid[b.dataset.needs!];
    });
  }

  function go(step: ContactStep) {
    opts.onStep?.(step as never);
    state.step = step;
    state.error = null;
    render();
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  async function findAddresses() {
    state.busy = true;
    state.searched = false;
    state.error = null;
    render();
    try {
      state.addresses = await api.searchAddresses(state.postcode.trim());
    } catch {
      state.addresses = [];
      state.error = "Something went wrong looking up your postcode. Please try again.";
    }
    state.busy = false;
    state.searched = true;
    render();
  }

  async function send() {
    if (!ready() || !api.submitContact) return;
    state.busy = true;
    state.sending = true;
    render();
    try {
      await api.submitContact({
        name: state.name.trim(),
        email: state.email.trim(),
        phone: state.phone.trim(),
        message: state.message.trim(),
        topic: state.topic || null,
        address: branding.form.askAddress ? state.address : null,
        consent: state.consent,
      });
      state.busy = false;
      state.sending = false;
      go("done");
    } catch {
      state.busy = false;
      state.sending = false;
      state.error = "We couldn't send your message. Please try again.";
      render();
    }
  }

  // ─── Events ────────────────────────────────────────────────────────────────

  function onClick(e: Event) {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;
    switch (el.dataset.action) {
      case "open":
        state.open = true;
        api.track?.("opened");
        return render();
      case "close":
        state.open = false;
        return render();
      case "find":
        return void findAddresses();
      case "pick-address":
        state.address = state.addresses.find((a) => a.id === el.dataset.id) ?? null;
        return render();
      case "change-address":
        state.address = null;
        return render();
      case "restart":
        Object.assign(state, { ...blank(), open: true });
        return go("form");
    }
  }

  function onInput(e: Event) {
    const el = e.target as HTMLInputElement;
    const model = el.dataset.model;
    if (!model) return;
    if (model === "consent") state.consent = el.checked;
    else (state as unknown as Record<string, string>)[model] = el.value;
    syncDisabled();
  }

  function onSubmit(e: Event) {
    e.preventDefault();
    void send();
  }

  // Enter in the postcode box searches instead of submitting the whole form
  function onKey(e: Event) {
    const k = e as KeyboardEvent;
    if (k.key === "Enter" && (k.target as HTMLElement).id === "xqc-postcode") {
      k.preventDefault();
      if (POSTCODE_RE.test(state.postcode.trim())) void findAddresses();
    }
  }

  shadow.addEventListener("click", onClick);
  shadow.addEventListener("input", onInput);
  shadow.addEventListener("change", onInput);
  shadow.addEventListener("submit", onSubmit);
  shadow.addEventListener("keydown", onKey);
  render();

  return {
    update(next: WidgetBranding) {
      branding = next;
      // An address picked before the look-up was switched off no longer counts
      if (!branding.form.askAddress) state.address = null;
      render();
    },
    async goTo(target) {
      // Preview: show the form, or the thank-you screen with sample details
      state.open = true;
      if (target === "done") Object.assign(state, { name: "Alex Morgan", email: "alex@example.com", message: "Hello", consent: true });
      go(target === "done" ? "done" : "form");
    },
    destroy() {
      shadow.removeEventListener("click", onClick);
      shadow.removeEventListener("input", onInput);
      shadow.removeEventListener("change", onInput);
      shadow.removeEventListener("submit", onSubmit);
      shadow.removeEventListener("keydown", onKey);
      shadow.innerHTML = "";
    },
  };
}
